import React, {useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {AuthBanner, AuthHandoff, AuthUI as S} from '~/src/components/auth/shell'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {buildIopAuthorizeUrl, isIopEnabled} from '~/src/utils/iop-auth'

/**
 * Welcome email landing page for IOP (Single Sign) registration flow.
 *
 * When a new user is onboarded, the backend sends a welcome email with a link to
 * /oauth/welcome?email=user@example.com&first_name=..&last_name=..&phone_number=..
 * (the name/phone params are optional — KRAF-711). This page:
 *
 * 1. Reads the user's email plus the allowlisted prefill params from the URL
 * 2. Generates PKCE (code_verifier + code_challenge) and stores in sessionStorage
 * 3. Builds the IOP OAuth authorize URL with:
 *    - login_hint={email} — IOP routes by account existence (existing account →
 *      sign-in, unknown → registration) and pre-fills + LOCKS the email field
 *    - first_name/last_name/phone_number — pre-fill the registration form (editable)
 *    - action=register — fallback page if IOP can't determine account existence
 * 4. Redirects the user to IOP's hosted page
 *
 * Only the three allowlisted prefill params are forwarded — never the whole query
 * string, so a crafted link can't inject arbitrary params into the IdP request.
 *
 * After the user completes registration on IOP, they are redirected back to
 * /oauth/callback where the normal IOP login flow (code exchange, user lookup,
 * permissions, dashboard navigation) takes over.
 *
 * Same pattern exists in SHR-FE (OAuthWelcome.tsx).
 */
export default function OAuthWelcome() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function redirect() {
      const email = searchParams.get('email')

      if (!isIopEnabled()) {
        window.location.href = '/signin'
        return
      }

      if (!email) {
        setError('Invalid welcome link — missing email parameter.')
        return
      }

      try {
        // Allowlisted signup prefills from the welcome email (KRAF-711) — forwarded
        // to IOP so the registration form comes pre-filled; the email itself is
        // locked by IOP via login_hint.
        const firstName = searchParams.get('first_name') ?? undefined
        const lastName = searchParams.get('last_name') ?? undefined
        const phoneNumber = searchParams.get('phone_number') ?? undefined

        // buildIopAuthorizeUrl generates PKCE, stores verifier + state in sessionStorage,
        // and returns the full IOP authorize URL with login_hint pre-filled
        const url = await buildIopAuthorizeUrl(email, {firstName, lastName, phoneNumber})
        // Append action=register as the fallback page when IOP can't determine
        // whether the account exists (IOP otherwise routes by existence)
        window.location.href = `${url}&action=register`
      } catch {
        setError('Failed to build authorization URL. Please try again.')
      }
    }

    void redirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <SignInContainer>
        <S.View>
          <S.CardTitle>Welcome</S.CardTitle>
          <AuthBanner banner={{tone: 'error', message: error}} />
          <S.TextLink as="a" href="/signin">
            Go to Sign In
          </S.TextLink>
        </S.View>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer>
      <S.View>
        <AuthHandoff direction="out" />
        <S.CardTitle>Welcome!</S.CardTitle>
        <S.Lede>Redirecting you to Staffology single sign-on...</S.Lede>
      </S.View>
    </SignInContainer>
  )
}
